// dialog/BgtAtrzRtsoOvrlExpndDialog.tsx
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

import Button from "@/js/common/ui/Button";
import SelectBox from "@/js/common/ui/input/SelectBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import TextBox from "@/js/common/ui/input/TextBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";

import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";

import BudgetViewFile from "../../budgetFileBox/BudgetViewFile";
import { budgetFilesProps } from "@/js/common/types/types";

import BgtAtrzRejectReason from "./BgtAtrzRejectReason";
import BgtOvrlEmpGrnamtViewDialog from "./BgtOvrlEmpGrnamtViewDialog";
import BgtOvrlGrnamtViewDialog from "./BgtOvrlGrnamtViewDialog";

interface Props {
  ovrlMbsdsExpndDocId: string;
  atrzId: string;
  atrzLineId: string;
  setCallTime: Function;
  onDone?: () => void;
}

const BgtAtrzRtsoOvrlExpndDialog = ({ ovrlMbsdsExpndDocId, atrzId, atrzLineId, setCallTime, onDone}: Props) => {
  const [expndRsdocNo      , setExpndRsdocNo      ] = React.useState<string>(""); // 결의서 문서번호
  const [orgCd, setOrgCd] = React.useState<string>("");
  const [implDt, setImplDt] = React.useState<string>("");
  const [bgtSbjct1stCd, setBgtSbjct1stCd] = React.useState<string>("");
  const [bgtSbjct2stCd, setBgtSbjct2stCd] = React.useState<string>("");
  const [bgtSbjct3stCd, setBgtSbjct3stCd] = React.useState<string>("");
  const [abstrCn, setAbstrCn] = React.useState<string>("");
  const [memo, setMemo] = React.useState<string>("");
  const [implAmt, setImplAmt] = React.useState<string>("");
  const [atrzSttsCd, setAtrzSttsCd] = React.useState<string>("");
  const [atrzOpnnCn, setAtrzOpnnCn] = React.useState<string>("");
  const [grnamtSeCd, setGrnamtSeCd] = React.useState<string>(""); // 교부금 구분

  const [savedExpndFiles, setSavedExpndFiles] = React.useState<budgetFilesProps[]>([]);

  const [grnamtSum   , setGrnamtSum   ] = React.useState<string>(""); // 교부금 합계
  const [empGrnamtSum, setEmpGrnamtSum] = React.useState<string>(""); // 우정실무원 교부금 합계
  const [sumTotal    , setSumTotal    ] = React.useState<string>(""); // 교부금 전체(교부금 + 우정실무원) 합계

  const [confirming, setConfirming] = React.useState(false);

  const [grnamtModalOpen, setGrnamtModalOpen] = React.useState(false);
  const [empGrnamtModalOpen, setEmpGrnamtModalOpen] = React.useState(false);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);

  const [hoveredCell, setHoveredCell] = React.useState<string | null>(null);

  const handleReset = () => {
    setOrgCd("");
    setExpndRsdocNo("");
    setImplDt("");
    setBgtSbjct1stCd("");
    setBgtSbjct2stCd("");
    setBgtSbjct3stCd("");
    setAbstrCn("");
    setMemo("");
    setImplAmt("");
    setAtrzSttsCd("");
    setAtrzOpnnCn("");
    setSavedExpndFiles([]);
    setGrnamtSum("");
    setEmpGrnamtSum("");
    setSumTotal("");
  };

  const loadDetail = (linkDocId: string) => {
    if (Util.isEmpty(linkDocId)) {
      handleReset();
      return;
    }

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/ovrl/select-aprvr-ovrl-expnd-dtl.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        atrzId,
        atrzLineId,
        atrzLinkDocId: linkDocId,
      },
      onSuccessFn: (res: any) => {
        setExpndRsdocNo (res.result.item.expndRsdocNo ); // 결의서 문서번호
        setOrgCd        (res.result.item.orgCd        ); // 조직
        setImplDt       (res.result.item.implDt       ); // 행위년월일(집행일자)
        setBgtSbjct1stCd(res.result.item.bgtSbjct1stCd); // 1차 코드(관)
        setBgtSbjct2stCd(res.result.item.bgtSbjct2stCd); // 2차 코드(항)
        setBgtSbjct3stCd(res.result.item.bgtSbjct3stCd); // 3차 코드(목)
        setAbstrCn      (res.result.item.abstrCn      ); // 적요
        setMemo         (res.result.item.memo         ); // 메모
        setImplAmt      (res.result.item.implAmt      ); // 집행금액
        setAtrzSttsCd   (res.result.item.atrzSttsCd   ); // 결재상태정보
        setAtrzOpnnCn   (res.result.item.atrzOpnnCn   ); // 반려사유(결재의견)
        setGrnamtSeCd   (res.result.item.grnamtSeCd   ); // 교부금 구분

        setGrnamtSum   (res.result.listGrnamtSum.sumDtbnAmt); // 교부금 합계
        setEmpGrnamtSum(res.result.listEmpGrnamtSum.dtbnAmt); // 우정실무원 교부금 합계
        setSumTotal(res.result.listGrnamtSum.sumDtbnAmt + res.result.listEmpGrnamtSum.dtbnAmt); // 교부금 전체  합계

        setSavedExpndFiles(res?.result?.listFile || res?.result?.files || []);

        AppContext.hideSpinner();
      },
    });
  };

  React.useEffect(() => {
    loadDetail(ovrlMbsdsExpndDocId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovrlMbsdsExpndDocId]);

  const validateBase = () => {
    if (Util.isEmpty(atrzId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(atrzLineId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzLineId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(ovrlMbsdsExpndDocId)) {
      AppContext.openAlert({ title: "알림", msg: "대상이 선택되지 않았습니다.", icon: "warning" });
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (confirming) return;
    if (!validateBase()) return;

    setConfirming(true);
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/atrz/process-aprvr-idnty-rjct.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        atrzId: String(atrzId),
        atrzLineId: String(atrzLineId),
        atrzLinkDocId: String(ovrlMbsdsExpndDocId),
        atrzLinkDocIdntySeCd: "01",
      },
      onSuccessFn: () => {
        AppContext.hideSpinner();
        setConfirming(false);
        AppContext.openAlert({ title: "알림", msg: "확인 처리되었습니다.", icon: "info" });
        if (typeof setCallTime === "function") setCallTime(new Date());
        onDone?.();
      },
    });
  };

  const openReject = () => {
    if (!validateBase()) return;
    setRejectModalOpen(true);
  };

  return (
    <div className="w100p dpFlx jcC pt5">
      <div className="dpFlx aiFs">
        <div className="w400">
          <div>
              <span className="fm-b fs16 fc007bff ls1 fw700">지출결의서 상세내용</span>
              <span className="fm-b fs13 ls06 fc777777" style={{marginLeft: "110px"}}>{expndRsdocNo}</span>
          </div>
          
          <div className="dpFlx mb5 pt10" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">행위년월일</span>
              </div>
              <DatePickerBox
                value={implDt}
                width={196}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                bgColor="#F6F6F6"
                textAlign="left"
                calendarThemeColor={AppContext.themeColor}
                onChange={(data: string) => setImplDt(data)}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">금액 (원)</span>
              </div>
              <div className="h36 dpFlx aiC bsBdb" style={{ width: 196 }}>
                <NumberTextBox
                  value={implAmt}
                  className="h36 bd1 bdrd2 bgcFFE6CE"
                  style={{ width: 196 }}
                  thousandsGroupStyle="thousand"
                  thousandSeparator=","
                  placeholder="0"
                  fontSize={14}
                  fontFamily="fm-c"
                  letterSpacing={-1}
                  inputClass="taR mr10"
                  onChange={(v: string) => setImplAmt(v)}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          <div className="dpFlx mb5" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">관</span>
              </div>
              <SelectBox
                value={bgtSbjct1stCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt1CodeArray("02", orgCd))}
                onChangeFunc={(v: string) => {
                  setBgtSbjct1stCd(v);
                  setBgtSbjct2stCd("");
                  setBgtSbjct3stCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">항</span>
              </div>
              <SelectBox
                value={bgtSbjct2stCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt2CodeArray(bgtSbjct1stCd, orgCd))}
                onChangeFunc={(v: string) => {
                  setBgtSbjct2stCd(v);
                  setBgtSbjct3stCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">목</span>
              </div>
              <SelectBox
                value={bgtSbjct3stCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt3CodeArray(bgtSbjct2stCd, orgCd))}
                onChangeFunc={(v: string) => setBgtSbjct3stCd(v)}
                disabled={true}
              />
            </div>
          </div>

          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">적요</span>
            </div>
            <TextBox
              value={abstrCn}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setAbstrCn(t)}
              mode="disabled"
            />
          </div>

          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">메모</span>
            </div>
            <TextBox
              value={memo}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setMemo(t)}
              mode="disabled"
            />
          </div>

          <BudgetViewFile fileSeCd="C02" savedBudgetFiles={savedExpndFiles} width="400px" height="70px" />

          {atrzSttsCd === "05" && (
            <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">반려</span>
              </div>
              <div className="dpFlx aiFs">
                <div className="flxg1 bsBdb">
                  <textarea
                    value={atrzOpnnCn}
                    className="h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs14 fc333333 ls1 bgcWhite rszN"
                    style={{ width: "400px" }}
                    maxLength={2000}
                    placeholder=""
                    onChange={(e) => setAtrzOpnnCn(e.target.value)}
                    readOnly={true}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="h30 dpFlx jcSb aiE pb5 mt5" style={{ width: 400 }}>
            <span className="fm-b fs13 ls06 fc111111 mb5">조합비 지출 내역</span>
          </div>

          <div className="w100p ofxA">
            <div style={{ minWidth: 320 }}>
                <div className="">
                    <table style={{borderCollapse: "collapse", width: "100%"}}>
                        <colgroup>
                            <col className="w100"></col>
                            <col className="w120"></col>
                            <col className="w100"></col>
                        </colgroup>
                        <thead>
                            <tr className="bgcf0f8ff h30">
                                <th className="fm-a fs12 fc555555" style={{border: "1px solid #ddd"}}>교부금</th>
                                <th className="fm-a fs12 fc555555" style={{border: "1px solid #ddd"}}>우정실무원교부금</th>
                                <th className="fm-a fs12 fc555555" style={{border: "1px solid #ddd"}}>합계</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {grnamtSeCd === "01" ?
                                    <td className="h28 taC fm-c fs12 fc828282"
                                        style={{
                                                border: "1px solid #ddd",
                                                cursor: "pointer",
                                                backgroundColor: hoveredCell === "A" ? "#C0E6F4" : "transparent",
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={() => setHoveredCell("A")}
                                            onMouseLeave={() => setHoveredCell(null)}
                                            onClick={() => {
                                                setGrnamtModalOpen(true);
                                            }}>
                                            {Util.comma(String(grnamtSum))}
                                    </td>
                                :
                                    <td className="h28 taC fm-c fs12 fcBDBDBD" style={{border: "1px solid #ddd", backgroundColor: "#F6F6F6"}}>0</td>
                                }    
                                {grnamtSeCd === "02" ?
                                    <td className="h28 taC fm-c fs12 fc828282"
                                        style={{
                                                border: "1px solid #ddd",
                                                cursor: "pointer",
                                                backgroundColor: hoveredCell === "B" ? "#C0E6F4" : "transparent",
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={() => setHoveredCell("B")}
                                            onMouseLeave={() => setHoveredCell(null)}
                                            onClick={() => setEmpGrnamtModalOpen(true)}>
                                            {Util.comma(String(empGrnamtSum))}
                                    </td>
                                :
                                    <td className="h28 taC fm-c fs12 fcBDBDBD" style={{border: "1px solid #ddd", backgroundColor: "#F6F6F6"}}>0</td>
                                }    
                                <td className="h28 taC fm-c fs12 fc828282 taR pr5" style={{border: "1px solid #ddd"}}>{Util.comma(String(sumTotal))}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

          <div className="dpFlx jcR mt25 gap10">
            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="crimson"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName="반려"
              onClickFunc={openReject}
              disabled={confirming}
            />
            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="#007bff"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName={confirming ? "처리중..." : "확인"}
              onClickFunc={handleConfirm}
              disabled={confirming}
            />
          </div>
        </div>

        <Modal
          modalOpen={rejectModalOpen}
          dim={true}
          visible={true}
          titleEl={<DialogTitleBar title="반려 사유 입력" backgroundColor={AppContext.themeColor} />}
          onCloseFunc={() => setRejectModalOpen(false)}
        >
          <BgtAtrzRejectReason
            atrzId={String(atrzId)}
            atrzLineId={String(atrzLineId)}
            atrzLinkDocId={String(ovrlMbsdsExpndDocId)}
            setModalOpen={setRejectModalOpen}
            setCallTime={setCallTime}
            onDone={onDone}
          />
        </Modal>

        <Modal
          modalOpen={grnamtModalOpen}
          dim={true}
          visible={true}
          titleEl={<DialogTitleBar title="교부금 배정" backgroundColor={AppContext.themeColor} />}
          onCloseFunc={() => setGrnamtModalOpen(false)}
        >
          <BgtOvrlGrnamtViewDialog ovrlMbsdsExpndDocId={ovrlMbsdsExpndDocId} setModalOpen={setGrnamtModalOpen} />
        </Modal>

        <Modal
          modalOpen={empGrnamtModalOpen}
          dim={true}
          visible={true}
          titleEl={<DialogTitleBar title="우정 실무원 교부금 배정" backgroundColor={AppContext.themeColor} />}
          onCloseFunc={() => setEmpGrnamtModalOpen(false)}
        >
          <BgtOvrlEmpGrnamtViewDialog
            ovrlMbsdsExpndDocId={ovrlMbsdsExpndDocId}
            setModalOpen={setEmpGrnamtModalOpen}
          />
        </Modal>
      </div>
    </div>
  );
};

export default BgtAtrzRtsoOvrlExpndDialog;
